import type { ReactNode } from 'react';
import type { BallZone } from '../../lib/afl/ball-position.js';

export type FieldProps = {
  leftLabel: string;
  rightLabel: string;
  leftIsYou: boolean;
  rightIsYou: boolean;
  ballX: number;
  ballY: number;
  highlight?: BallZone;
  /** Rendered as an overlay at (ballX, ballY) in 0..100 percent space. */
  ballOverlay?: ReactNode;
  /**
   * Full-bleed overlay layer (0..100% positioned children own their own
   * placement). Used for the on-field player cards. Rendered beneath the
   * ball overlay so the carrier's football reads on top.
   */
  overlay?: ReactNode;
  /**
   * Interactive layer for click-to-move disposal targets. The wrapper is
   * pointer-events-none so empty ground stays inert; each marker opts back
   * in with pointer-events-auto. Sits above the players and the ball so a
   * target on the carrier's cell is still clickable.
   */
  targets?: ReactNode;
  /**
   * Inspect a position: tap a printed field cell to see which cards each coach
   * has standing there. Fires with the cell's grid column + row so the caller
   * can map it back to an engine zone. Empty cells are clickable too.
   */
  onCellClick?: (cell: { col: number; row: 'top' | 'centre' | 'bottom'; label: string }) => void;
};

/**
 * Horizontal AFL ground with all 15 named field cells visible.
 *
 * Drawn in a TRUE-ASPECT 170×100 viewBox (the container locks itself to
 * 17:10 — see AflFieldView), so circles are circles and labels never
 * stretch. Horizontal coordinates use the X() scaler: overlay layers
 * (players / ball / targets) keep working in 0..100 *percent* space, and
 * X() maps that space onto the wide viewBox for SVG geometry only.
 *
 * This component is the canonical reference for cell ids. Engine state
 * that places players uses the same identifiers.
 */

/** Percent-space → viewBox-space for horizontal SVG coordinates. */
const VIEW_W = 170;
const VIEW_H = 100;
const X = (pct: number) => (pct * VIEW_W) / 100;

// Each cell sits in a 16-column × 3-row grid laid over the oval.
// Defensive third spans columns 1-6, midfield 7-9, attack 10-15.
// "side" is which sideline edge a cell hugs ('top', 'centre', 'bottom').
type Cell = {
  id: string;
  label: string;
  short: string;
  col: number; // 1..15
  row: 'top' | 'centre' | 'bottom';
  /** Whether this cell is a "spine" position (central) or boundary. */
  band: 'spine' | 'boundary';
};

// IMPORTANT: this is the ground from YOUR perspective. Right = your goal.
// "Defensive" = your defensive third (RIGHT side, cols 10-15).
// "Attack"    = your attack third       (LEFT  side, cols 1-6).
// Midfield is cols 7-9 (narrower).
export const FIELD_CELLS: ReadonlyArray<Cell> = [
  // Your attack third — opponent's defensive third, LEFT side of the screen.
  // Full forward line at the leftmost edge: forward-pocket, full-forward, forward-pocket.
  { id: 'opp_fb_pocket_top',       label: 'Back pocket',          short: 'BP',  col: 1, row: 'top',    band: 'boundary' },
  { id: 'opp_full_back',           label: 'Full back',            short: 'FB',  col: 1, row: 'centre', band: 'spine' },
  { id: 'opp_fb_pocket_bottom',    label: 'Back pocket',          short: 'BP',  col: 1, row: 'bottom', band: 'boundary' },
  { id: 'opp_hb_flank_top',        label: 'Half-back flank',      short: 'HBF', col: 4, row: 'top',    band: 'boundary' },
  { id: 'opp_centre_half_back',    label: 'Centre half-back',     short: 'CHB', col: 4, row: 'centre', band: 'spine' },
  { id: 'opp_hb_flank_bottom',     label: 'Half-back flank',      short: 'HBF', col: 4, row: 'bottom', band: 'boundary' },
  // Midfield — narrower third.
  { id: 'wing_top',                label: 'Wing',                 short: 'W',   col: 8, row: 'top',    band: 'boundary' },
  { id: 'centre',                  label: 'Centre',               short: 'C',   col: 8, row: 'centre', band: 'spine' },
  { id: 'wing_bottom',             label: 'Wing',                 short: 'W',   col: 8, row: 'bottom', band: 'boundary' },
  // Your attack-side lines.
  { id: 'hf_flank_top',            label: 'Half-forward flank',   short: 'HFF', col: 12, row: 'top',    band: 'boundary' },
  { id: 'centre_half_forward',     label: 'Centre half-forward',  short: 'CHF', col: 12, row: 'centre', band: 'spine' },
  { id: 'hf_flank_bottom',         label: 'Half-forward flank',   short: 'HFF', col: 12, row: 'bottom', band: 'boundary' },
  { id: 'ff_pocket_top',           label: 'Forward pocket',       short: 'FP',  col: 15, row: 'top',    band: 'boundary' },
  { id: 'full_forward',            label: 'Full forward',         short: 'FF',  col: 15, row: 'centre', band: 'spine' },
  { id: 'ff_pocket_bottom',        label: 'Forward pocket',       short: 'FP',  col: 15, row: 'bottom', band: 'boundary' },
];

// Cell labels sit on the SAME grid the ball / players use (see zoneToXY in
// field-coords) so a token lands exactly on its cell label instead of
// beside it. The five line-columns (1,4,8,12,15 → d50,hb,centre,hf,f50) map
// to the zoneToXY x-stops, and rows map to its lane y-stops.
function cellX(col: number): number {
  const byCol: Record<number, number> = { 1: 14, 4: 32, 8: 50, 12: 68, 15: 86 };
  return byCol[col] ?? 50;
}
function cellY(row: 'top' | 'centre' | 'bottom'): number {
  return row === 'top' ? 30 : row === 'bottom' ? 70 : 50;
}

export function Field({
  leftLabel,
  rightLabel,
  leftIsYou,
  rightIsYou,
  ballX,
  ballY,
  highlight,
  ballOverlay,
  overlay,
  targets,
  onCellClick,
}: FieldProps) {
  return (
    <div
      data-drop-arena
      // A size container, so overlays can be measured in `cqw` and scale
      // WITH the ground instead of staying a fixed number of pixels while
      // the oval letterboxes down on a phone.
      className="relative h-full w-full overflow-hidden rounded-2xl border border-[#13251a] bg-gradient-to-br from-[#2c4a33] via-[#264229] to-[#172d1f] shadow-[0_16px_40px_-16px_rgba(0,0,0,0.9)] [container-type:inline-size]"
    >
      {/* Mowing stripes. */}
      <div className="absolute inset-0 opacity-20 [background-image:repeating-linear-gradient(90deg,rgba(0,0,0,0.3)_0px,rgba(0,0,0,0.3)_10px,transparent_10px,transparent_20px)]" />
      {/* Soft stadium lighting from above + edge vignette. */}
      <div className="absolute inset-0 [background:radial-gradient(ellipse_60%_55%_at_50%_38%,rgba(255,255,255,0.10),transparent_70%)]" />
      <div className="absolute inset-0 [background:radial-gradient(ellipse_at_center,transparent_55%,rgba(0,0,0,0.5)_100%)]" />

      <svg
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        preserveAspectRatio="none"
        className="absolute inset-0 h-full w-full"
      >
        {/* Concentric mowing rings inside the boundary. */}
        <ellipse cx={X(50)} cy={50} rx={X(41)} ry={39} fill="none" stroke="rgba(255,255,255,0.04)" strokeWidth={7} />
        <ellipse cx={X(50)} cy={50} rx={X(30)} ry={29} fill="none" stroke="rgba(0,0,0,0.06)" strokeWidth={7} />
        <ellipse cx={X(50)} cy={50} rx={X(19)} ry={19} fill="none" stroke="rgba(255,255,255,0.04)" strokeWidth={7} />

        {/* Boundary oval (uses the full viewBox so the ground feels big). */}
        <ellipse cx={X(50)} cy={50} rx={X(48)} ry={46} fill="none" stroke="#fafafa" strokeWidth={0.7} strokeOpacity={0.9} />

        {/* Third-line dividers. Midfield is the narrow band in the middle. */}
        <line x1={X(45)} x2={X(45)} y1={7} y2={93} stroke="#fafafa" strokeOpacity={0.22} strokeWidth={0.35} strokeDasharray="3 3" />
        <line x1={X(55)} x2={X(55)} y1={7} y2={93} stroke="#fafafa" strokeOpacity={0.22} strokeWidth={0.35} strokeDasharray="3 3" />

        {/* Centre square + circle. */}
        <rect
          x={X(47)}
          y={42}
          width={X(6)}
          height={16}
          fill={highlight === 'centre' ? 'rgba(250,204,21,0.18)' : 'none'}
          stroke="#fafafa"
          strokeOpacity={0.55}
          strokeWidth={0.35}
        />
        <circle cx={X(50)} cy={50} r={3.2} fill="none" stroke="#fafafa" strokeOpacity={0.55} strokeWidth={0.35} />
        <circle cx={X(50)} cy={50} r={1} fill="#fafafa" fillOpacity={0.6} />

        {/* 50m arcs into each end. */}
        <path
          d={`M ${X(17)} 26 A 27 27 0 0 0 ${X(17)} 74`}
          fill={highlight === 'left_50' ? 'rgba(250,204,21,0.16)' : 'none'}
          stroke="#fafafa"
          strokeOpacity={0.6}
          strokeWidth={0.35}
        />
        <path
          d={`M ${X(83)} 26 A 27 27 0 0 1 ${X(83)} 74`}
          fill={highlight === 'right_50' ? 'rgba(250,204,21,0.16)' : 'none'}
          stroke="#fafafa"
          strokeOpacity={0.6}
          strokeWidth={0.35}
        />

        {/* Goal squares. */}
        <rect x={X(2)} y={44.5} width={6.5} height={11} fill="none" stroke="#fafafa" strokeOpacity={0.5} strokeWidth={0.3} />
        <rect x={X(98) - 6.5} y={44.5} width={6.5} height={11} fill="none" stroke="#fafafa" strokeOpacity={0.5} strokeWidth={0.3} />

        {/* Cell markers. */}
        {FIELD_CELLS.map((cell) => {
          const cx = X(cellX(cell.col));
          const cy = cellY(cell.row);
          const isSpine = cell.band === 'spine';
          return (
            <g
              key={cell.id}
              onClick={onCellClick ? () => onCellClick({ col: cell.col, row: cell.row, label: cell.label }) : undefined}
              style={onCellClick ? { cursor: 'pointer' } : undefined}
            >
              {/* Generous transparent hit-area so the small marker is easy to tap. */}
              {onCellClick && <circle cx={cx} cy={cy} r={7} fill="transparent" />}
              <circle
                cx={cx}
                cy={cy}
                r={isSpine ? 4.6 : 4}
                fill={isSpine ? 'rgba(250,204,21,0.10)' : 'rgba(255,255,255,0.06)'}
                stroke={isSpine ? 'rgba(250,204,21,0.55)' : 'rgba(255,255,255,0.35)'}
                strokeWidth={0.35}
                style={{ pointerEvents: 'none' }}
              />
              <text
                x={cx}
                y={cy + 0.4}
                fontSize={3}
                textAnchor="middle"
                dominantBaseline="middle"
                fill={isSpine ? '#fde68a' : '#e2e8f0'}
                style={{ pointerEvents: 'none', fontWeight: 700, letterSpacing: '0.04em' }}
              >
                {cell.short}
              </text>
            </g>
          );
        })}

        {/* Goal posts: left (opponent's), right (you). */}
        <GoalPosts x={X(2)} />
        <GoalPosts x={X(98)} flip />
      </svg>

      <EndLabel position="left" label={leftLabel} isYou={leftIsYou} />
      <EndLabel position="right" label={rightLabel} isYou={rightIsYou} />

      {/* On-field player layer — children position themselves at 0..100%. */}
      {overlay && <div className="pointer-events-none absolute inset-0">{overlay}</div>}

      {/*
        Disposal target layer — above the players so a marker on the
        carrier's own cell still receives the click. The layer ignores
        pointer events; individual markers re-enable them.
      */}
      {targets && <div className="pointer-events-none absolute inset-0 z-30">{targets}</div>}

      {ballOverlay && (
        <div
          className="pointer-events-none absolute w-[15cqw] min-w-[2.75rem] max-w-[5rem] transition-[left,top] duration-700 ease-out will-change-[left,top] motion-reduce:transition-none"
          style={{ left: `${ballX}%`, top: `${ballY}%`, transform: 'translate(-50%, -50%)' }}
        >
          {ballOverlay}
        </div>
      )}
    </div>
  );
}

/**
 * Top-down goal-line furniture: the goal line itself plus the four post
 * bases — big pair = goal posts, small outer pair = behind posts.
 */
function GoalPosts({ x, flip = false }: { x: number; flip?: boolean }) {
  void flip; // symmetric top-down rendering; kept for call-site clarity
  return (
    <g>
      <line x1={x} x2={x} y1={41} y2={59} stroke="#fafafa" strokeWidth={0.45} strokeOpacity={0.85} />
      <circle cx={x} cy={42.5} r={0.8} fill="#fde68a" stroke="rgba(0,0,0,0.5)" strokeWidth={0.2} />
      <circle cx={x} cy={47} r={1.2} fill="#fafafa" stroke="rgba(0,0,0,0.5)" strokeWidth={0.2} />
      <circle cx={x} cy={53} r={1.2} fill="#fafafa" stroke="rgba(0,0,0,0.5)" strokeWidth={0.2} />
      <circle cx={x} cy={57.5} r={0.8} fill="#fde68a" stroke="rgba(0,0,0,0.5)" strokeWidth={0.2} />
    </g>
  );
}

function EndLabel({
  position,
  label,
  isYou,
}: {
  position: 'left' | 'right';
  label: string;
  isYou: boolean;
}) {
  const positional = position === 'left' ? 'left-2' : 'right-2';
  return (
    <div
      className={`absolute top-1/2 -translate-y-1/2 ${positional} rounded-md border px-2 py-0.5 font-display text-[10px] font-semibold uppercase tracking-widest shadow-lg shadow-black/40 backdrop-blur-sm ${
        isYou ? 'border-ec-ruck/70 bg-[rgba(62,68,77,0.30)] text-ec-face' : 'border-ec-oxblood/70 bg-[rgba(39,147,211,0.30)] text-ec-face'
      }`}
    >
      {label}
      {isYou && ' · You'}
    </div>
  );
}
